import React, { forwardRef } from 'react';

export type ActionButtonVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'neutral'
  | 'ghost';

export type ActionButtonSize = 'sm' | 'md' | 'lg' | 'icon';

const VARIANT_CLASSES: Record<ActionButtonVariant, string> = {
  primary:
    'border-primary-600 bg-primary-600 text-white shadow-sm hover:border-primary-700 hover:bg-primary-700',
  secondary:
    'border-primary-200 bg-white text-primary-800 hover:border-primary-300 hover:bg-primary-50',
  success:
    'border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100',
  danger:
    'border-red-200 bg-red-50 text-red-700 hover:border-red-300 hover:bg-red-100',
  warning:
    'border-amber-200 bg-amber-50 text-amber-800 hover:border-amber-300 hover:bg-amber-100',
  neutral:
    'border-slate-200 bg-white text-slate-700 hover:border-primary-200 hover:bg-primary-50 hover:text-primary-800',
  ghost:
    'border-transparent bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900',
};

const SIZE_CLASSES: Record<ActionButtonSize, string> = {
  sm: 'min-h-9 px-3 py-1.5 text-sm',
  md: 'min-h-10 px-4 py-2 text-sm',
  lg: 'min-h-11 px-5 py-2.5 text-sm',
  icon: 'h-10 w-10 p-0',
};

const joinClasses = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

export interface ActionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ActionButtonVariant;
  size?: ActionButtonSize;
  fullWidth?: boolean;
}

const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(
  (
    {
      variant = 'neutral',
      size = 'md',
      fullWidth = false,
      type = 'button',
      className,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      className={joinClasses(
        'inline-flex items-center justify-center gap-2 rounded-lg border font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 disabled:opacity-80 disabled:shadow-none',
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    />
  ),
);

ActionButton.displayName = 'ActionButton';

export default ActionButton;
